/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";

const SUBSTEPS_ANSWERS = ["YesGLP1", "YesOther"];

interface QuizAnswers {
  [key: string]: string | string[];
}

export interface QuizStepProps {
  handleStepData: (name: string, value: string | string[]) => void;
  data: QuizAnswers;
  errors: { [key: string]: string } | null;
  next?: () => void;
}

type UseQuestionnaireProps = {
  component: (props: QuizStepProps) => JSX.Element;
  name: string;
  subSteps: {
    component: (props: QuizStepProps) => JSX.Element;
    name: string;
  }[];
}[];

export function useQuestionnaire(steps: UseQuestionnaireProps) {
  const mainStepsLength = steps.length - 1;
  const [currentStep, setCurrentStep] = useState(0);
  const [subStep, setSubStep] = useState({ isSubStep: false, count: 0 });
  const [stepValues, setStepValues] = useState({} as QuizAnswers);
  const [progress, setProgress] = useState({
    questionsAnswered: 0,
    totalQuestions: mainStepsLength,
  });
  const [errors, setErrors] = useState<{ [key: string]: string } | null>(null);

  useEffect(() => {
    const savedStepValues = localStorage.getItem("glpQuestionnaire");
    const savedCurrentStep = localStorage.getItem("currentStep");
    const savedSubStep = localStorage.getItem("subStep");
    const savedIsSubStep = localStorage.getItem("isSubStep");
    const savedProgress = localStorage.getItem("progress");

    setStepValues(JSON.parse(savedStepValues ?? "{}"));
    setCurrentStep(parseInt(savedCurrentStep ?? "0"));
    setSubStep((prev) => ({
      ...prev,
      count: parseInt(savedSubStep ?? "0"),
      isSubStep: JSON.parse(savedIsSubStep ?? "false"),
    }));
    setProgress(
      JSON.parse(
        savedProgress ??
          `{"questionsAnswered": 0, "totalQuestions": ${mainStepsLength}}`
      )
    );
  }, [mainStepsLength]);

  const handleStepData = (key: string, value: string | string[]) => {
    setStepValues((prev) => {
      const newValues = { ...prev, [key]: value };
      localStorage.setItem("glpQuestionnaire", JSON.stringify(newValues));
      return newValues;
    });
  };

  // Helper function for type safety when accessing validation rules
  function getValidationRule(
    componentName: string,
    validationRules: ValidationRules
  ): ValidationRule {
    const rule = validationRules[componentName];
    if (!rule) {
      throw new Error(
        `No validation rule found for component: ${componentName}`
      );
    }
    return rule;
  }

  // Enhanced type-safe validation function
  function validateStep(
    componentName: string,
    stepValues: Record<string, string | string[]>,
    validationRules: ValidationRules,
    isSubStep: boolean,
    currentStep: number,
    subStepCount: number,
    steps: UseQuestionnaireProps
  ): Record<string, string> {
    let validationRule: ValidationRule;

    if (isSubStep) {
      // Get validation rules for the current substep
      const subStepComponent = steps[currentStep].subSteps[subStepCount];
      validationRule = getValidationRule(
        subStepComponent.name,
        validationRules
      );
    } else {
      // Get validation rules for the main step
      validationRule = getValidationRule(componentName, validationRules);
    }

    const errors: Record<string, string> = {};

    Object.keys(validationRule).forEach((field) => {
      const validation = validationRule[field];
      const error = validation(stepValues[field] || "", stepValues);
      if (error) {
        errors[field] = error;
      }
    });

    setErrors(errors);
    return errors;
  }

  const handleNext = () => {
    const isInvalid =
      Object.keys(
        validateStep(
          steps[currentStep].name,
          stepValues,
          validationRules,
          subStep.isSubStep,
          currentStep,
          subStep.count,
          steps
        )
      ).length > 0;

    if (isInvalid) {
      return;
    }

    if (subStep.isSubStep) {
      handleSubStepNavigation();
    } else if (
      steps[currentStep]?.subSteps?.length &&
      SUBSTEPS_ANSWERS.includes(stepValues["WeightLossMeds"] as string)
    ) {
      enterSubSteps();
    } else {
      moveToNextMainStep();
    }

    updateProgress(1);
  };

  const handlePrev = () => {
    if (subStep.isSubStep && subStep.count > 0) {
      moveToPreviousSubStep();
    } else if (subStep.isSubStep && subStep.count === 0) {
      const answerName = steps[currentStep].subSteps[subStep.count].name;
      handleStepData(answerName, "");
      exitSubSteps();
      localStorage.setItem(
        "progress",
        JSON.stringify({ ...progress, totalQuestions: mainStepsLength })
      );
      setProgress({
        ...progress,
        totalQuestions: mainStepsLength,
      });
    } else if (
      !subStep.isSubStep &&
      subStep.count > 0 &&
      steps[currentStep - 1].subSteps.length
    ) {
      setSubStep((prev) => ({ ...prev, isSubStep: true }));
      localStorage.setItem("isSubStep", "true");
      moveToPreviousMainStep();
    } else if (currentStep > 0) {
      moveToPreviousMainStep();
    }

    updateProgress(-1);
  };

  const handleSubStepNavigation = () => {
    const maxSubSteps = steps[currentStep].subSteps.length - 1;
    const shouldDoAllSubsteps = stepValues["WeightLossMeds"] === "YesGLP1";
    const subStepsToComplete = shouldDoAllSubsteps
      ? maxSubSteps
      : maxSubSteps - 1;

    if (subStep.count < subStepsToComplete) {
      setSubStep((prev) => {
        const newSubStep = prev.count + 1;
        localStorage.setItem("subStep", newSubStep.toString());
        return { ...prev, count: newSubStep };
      });
    } else {
      exitSubSteps();
      moveToNextMainStep();
    }
  };

  const enterSubSteps = () => {
    setSubStep({ isSubStep: true, count: 0 });
    localStorage.setItem("subStep", "0");
    localStorage.setItem("isSubStep", "true");
    const subStepsToAdd =
      stepValues["WeightLossMeds"] === "YesGLP1"
        ? steps[currentStep].subSteps.length
        : steps[currentStep].subSteps.length - 1;
    setProgress((prev) => ({
      ...prev,
      totalQuestions: mainStepsLength + subStepsToAdd,
    }));
  };

  const exitSubSteps = () => {
    setSubStep((prev) => ({ ...prev, isSubStep: false }));
    localStorage.setItem("isSubStep", "false");
  };

  const moveToNextMainStep = () => {
    setCurrentStep((prev) => {
      const newStep = prev + 1;
      localStorage.setItem("currentStep", newStep.toString());
      return newStep;
    });
  };

  const moveToPreviousMainStep = () => {
    setCurrentStep((prev) => {
      const newStep = prev - 1;
      localStorage.setItem("currentStep", newStep.toString());
      return newStep;
    });
  };

  const moveToPreviousSubStep = () => {
    const answerName = steps[currentStep].subSteps[subStep.count].name;
    handleStepData(answerName, "");
    setSubStep((prev) => {
      const newSubStep = Math.max(prev.count - 1, 0);
      localStorage.setItem("subStep", newSubStep.toString());
      return { ...prev, count: newSubStep };
    });
  };

  const updateProgress = (direction: 1 | -1) => {
    setProgress((prev) => {
      const totalQuestionsAnswered = Math.max(
        prev.questionsAnswered + direction,
        0
      );
      localStorage.setItem(
        "progress",
        JSON.stringify({
          questionsAnswered: totalQuestionsAnswered,
          totalQuestions: prev.totalQuestions,
        })
      );
      return {
        ...prev,
        questionsAnswered: totalQuestionsAnswered,
      };
    });
  };

  return {
    currentStep,
    subStep,
    stepValues,
    progress,
    handleStepData,
    handleNext,
    handlePrev,
    errors,
  };
}

// Define the type for validation functions
type ValidationFunction = (
  value: string | string[],
  allValues: Record<string, string | string[]>
) => string | null;

const requiredValidator: ValidationFunction = (
  value: string | string[],
  _allValues: Record<string, string | string[]>
) => {
  // Check if value exists and has length
  return value && value.length > 0 ? null : "Field is required";
};

// Conditional validator factory
const conditionalValidator = (
  dependentField: string,
  requiredValue: string,
  message = "Field is required"
): ValidationFunction => {
  return (
    value: string | string[],
    allValues: Record<string, string | string[]>
  ) => {
    if (Array.isArray(allValues[dependentField])) {
      return null;
    }
    // Only validate if the dependent field matches the required value
    if (requiredValue.includes(allValues[dependentField] as string)) {
      return value && value.length > 0 ? null : message;
    }
    return null; // Skip validation if condition isn't met
  };
};

// Create a flexible validation rule type that allows dynamic keys
type ValidationRule = {
  [key: string]: ValidationFunction;
};

// Define a type for the entire validation rules object
type ValidationRules = {
  [key: string]: ValidationRule;
};

// Define the validation rules for the questionnaire
// Use conditional validation for questions that have multiple answers
// and the conditional field is not the same as the question field
export const validationRules: ValidationRules = {
  Welcome: {
    Welcome: () => null,
  },
  WeightLossGoal: {
    Goal: () => null,
  },
  BirthGender: {
    Gender: requiredValidator,
  },
  HeightWeight: {
    Height: requiredValidator,
    Inches: requiredValidator,
    Weight: requiredValidator,
  },
  Diagnoses: {
    Diagnoses: requiredValidator,
  },
  WeightLossMeds: {
    WeightLossMeds: () => null,
  },
  WeightLossMedicationList: {
    WeightLossMedsList: requiredValidator,
  },
  StartingWeight: {
    StartingWeight: requiredValidator,
  },
  CurrentWeight: {
    CurrentWeight: requiredValidator,
  },
  WeightLossMedsThroughUs: {
    WeightLossMedsThroughUs: requiredValidator,
  },
  LastDoseWeightLossMeds: {
    LastDoseWeightLossMeds: requiredValidator,
    ImageUpload: conditionalValidator(
      "LastDoseWeightLossMeds",
      "5days 10days 14days"
    ),
  },
  CurrentMedications: {
    CurrentMedications: conditionalValidator(
      "CurrentMedicationsQuestion",
      "Yes"
    ),
  },
  WeightLossMedsInPast: {
    WeightLossMedsInPast: conditionalValidator(
      "WeightLossMedsInPastQuestion",
      "Yes"
    ),
  },
  OpiateMedications: {
    OpiateMedications: conditionalValidator("OpiateMedicationsQuestion", "Yes"),
  },
  PriorBariatricSurgery: {
    PriorBariatricSurgery: conditionalValidator(
      "PriorBariatricSurgeryQuestion",
      "Yes"
    ),
  },
  PreviousWLAttempts: {
    PreviousWLAttempts: conditionalValidator(
      "PreviousWLAttemptsQuestion",
      "Yes"
    ),
  },
  AreWillingToChange: {
    AreWillingToChange: requiredValidator,
  },
  WeightInLastYear: {
    WeightInLastYear: requiredValidator,
  },
  MedicalHistory: {
    MedicalHistory: requiredValidator,
  },
  DiabetesDiagnosis: {
    DiabetesDiagnosisQuestion: requiredValidator,
    RecentGlucose: conditionalValidator(
      "DiabetesDiagnosisQuestion",
      "Yes",
      "Please enter your recent glucose value"
    ),
    RecentHemoglobin: conditionalValidator(
      "DiabetesDiagnosisQuestion",
      "Yes",
      "Please enter your recent hemoglobin A1c value"
    ),
  },
  BloodPressure: {
    BloodPressure: requiredValidator,
  },
  HeartRate: {
    HeartRate: requiredValidator,
  },
  WeightBmiGoal: {
    GoalWeightBmi: requiredValidator,
  },
  FurtherNotes: {
    FurtherNotesQuestion: requiredValidator,
    FurtherNotes: conditionalValidator("FurtherNotesQuestion", "Yes"),
  },
  MedicationAllergies: {
    MedicationAllergiesQuestion: requiredValidator,
    MedicationAllergies: conditionalValidator(
      "MedicationAllergiesQuestion",
      "Yes"
    ),
  },
  Pharmacy: {
    pharmacy: requiredValidator,
  },
};

export const clearQuestionnaire = () => {
  localStorage.removeItem("glpQuestionnaire");
  localStorage.removeItem("currentStep");
  localStorage.removeItem("subStep");
  localStorage.removeItem("isSubStep");
  localStorage.removeItem("progress");
};
